import { Link } from 'react-router'
import { R } from './parts'

export function Bots() {
  return (
    <>
      <p>
        A bot takes a seat like any player. It is dealt a card, plays its night, talks in the chat during the
        day and votes. You can fill two empty seats with bots, or play a whole table of them alone.
      </p>

      <h2>Adding bots</h2>
      <p>
        In the lobby the host presses "Add a bot" for each empty seat. Bots are marked with a robot beside
        their name, can be moved in the seat order like anyone else, and the host can remove them again. A
        table needs three seats to start: one person and two bots is enough.
      </p>

      <h2>What a bot knows</h2>
      <p>
        Exactly what a player in its seat would know: the card it was dealt, what its own night showed it, and
        everything said at the table. It does not see the deal, the other players’ cards or their night
        actions, and the server never tells it more than it tells you. A <R id="seer" /> bot knows the one
        card it looked at. A <R id="werewolf" /> bot knows its partner.
      </p>

      <h2>How bots talk</h2>
      <ul>
        <li>
          <strong>They claim.</strong> Each bot puts a claim on its card and says what it did at night.
          Village bots tell the truth.
        </li>
        <li>
          <strong>The evil ones lie.</strong> A wolf bot picks a cover role from the deck, prefers one it saw
          in the center, avoids a role already claimed, and invents a night to go with it. It never frames its
          own partner.
        </li>
        <li>
          <strong>They reason.</strong> A truthful bot that saw a card where someone else claims it says so. A{' '}
          <R id="troublemaker" /> bot knows whose card it moved, and an <R id="insomniac" /> bot knows whether
          a claimed swap of its card really happened.
        </li>
        <li>
          <strong>They answer your claim.</strong> Claim a role and the bot holding that card, or one whose
          night contradicts you, will respond.
        </li>
        <li>
          <strong>They say whom they vote for,</strong> and then vote that way. A late claim can change a
          bot’s mind, and it says so.
        </li>
        <li>
          <strong>
            The <R id="tanner" /> bot acts suspicious,
          </strong>{' '}
          because that is how a Tanner wins.
        </li>
      </ul>

      <h2>Asking them questions</h2>
      <p>
        During the day you can write to the bots in the chat: name a bot, name a role, or just ask the table a
        question. A language model words the answer of one bot at a time. It plays that bot with what the bot
        knows (its card, its night, the claims and the conversation) and is told to protect its side: an
        honest bot shares what helps the village, a wolf bot keeps its cover, and neither gives up its card
        just because you asked for it.
      </p>
      <p>
        The same model chooses how each bot votes, from the whole day’s talk. If it is unavailable, the bots
        still play: they say their own lines and vote as their reasoning planned. What the chat sends to the
        model, and what it does not, is described in the <Link to="/privacy/">privacy policy</Link>.
      </p>

      <h2>When bots stay quiet</h2>
      <p>
        The chat is closed during the deal, the night and the vote, for bots as for people: everyone votes on
        their own. After the results, a few bots own up to their lies or gloat about them.
      </p>

      <h2>Getting the most out of a bot table</h2>
      <ul>
        <li>
          Start with the classic deck and three or four bots. See{' '}
          <Link to="/guides/decks/">decks for every table</Link>.
        </li>
        <li>
          Make your claim early. The bots hear claims in order and answer the ones that touch what they know.
        </li>
        <li>
          Ask direct questions and name the bot: "whose card did you look at?" gets a better answer than
          "anyone got anything?".
        </li>
        <li>
          Lie to them. A bot that knows your claim is false will say so, which is good practice for a table of
          people. Read <Link to="/guides/strategy/">the strategy guide</Link> first.
        </li>
        <li>
          Afterwards, open the Game Preview in the results to see the night the bots were reasoning about.
        </li>
      </ul>
    </>
  )
}
